import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  makeStyles,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

import { Organization } from '../../../organizations/types/organization.type';
import { useUpdateQuote } from '../../hooks/update-quote.hook';
import { Quote } from '../../types/quote.type';
import { resolveDefaultOwnerId } from '../../utils/resolve-default-owner-id.util';
import {
  CreateQuoteFormValues,
  CreateQuoteStepper,
} from '../Containers/CreateQuoteStepper.component';

type Props = {
  open: boolean;
  organization: Organization;
  quote: Quote;
  onUpdated: () => void;
  onClose: () => void;
};

const useStyles = makeStyles((theme) => ({
  title: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingRight: theme.spacing(1),
  },
  content: {
    minHeight: 520,
  },
}));

function toDateInputValue(isoDate: string): string {
  if (!isoDate) return '';
  return isoDate.length >= 10 ? isoDate.slice(0, 10) : isoDate;
}

function buildDefaultValues(quote: Quote, organization: Organization): Partial<CreateQuoteFormValues> {
  return {
    items: quote.items,
    globalDiscount: quote.globalDiscount,
    delivery: {
      date: quote.delivery.date,
      schedule: quote.delivery.schedule,
      deliveryPointId: quote.delivery.deliveryPoint.id,
    },
    validUntil: toDateInputValue(quote.validUntil),
    conditions: quote.conditions,
    ownerId: resolveDefaultOwnerId(quote.ownerId, organization),
  };
}

export function EditQuoteDialog({ open, organization, quote, onUpdated, onClose }: Props) {
  const classes = useStyles();
  const { updateQuote, isSubmitting } = useUpdateQuote();

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleSubmit = async (values: CreateQuoteFormValues) => {
    const updated = await updateQuote({
      id: quote.id,
      data: {
        items: values.items,
        globalDiscount: values.globalDiscount,
        delivery: values.delivery,
        validUntil: values.validUntil,
        conditions: values.conditions,
        ownerId: values.ownerId,
      },
    });
    if (updated) {
      onUpdated();
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth scroll="paper">
      <DialogTitle disableTypography className={classes.title}>
        <Typography variant="h6">{`Modifier le devis ${quote.number}`}</Typography>
        <IconButton onClick={handleClose} aria-label="close" disabled={isSubmitting}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers className={classes.content}>
        <CreateQuoteStepper
          organization={organization}
          defaultValues={buildDefaultValues(quote, organization)}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          submitLabel="Enregistrer"
          submittingLabel="Enregistrement…"
        />
      </DialogContent>
    </Dialog>
  );
}
